/**
 * TanStack Query hooks for /api/admin/assets/* (PM map screen, web Plan 03-04).
 *
 * Wire-format mirror of server/app/assets/schemas.GeoJsonProperties (D-72).
 * The cleaning_color / coating_color / etc. enum types are intentionally
 * uniform `green | yellow | red | grey` (the server's DimensionColor literal),
 * even though Phase 3's pure-function derivation only ever emits a subset for
 * each dimension — keeping the client union the same as the server contract
 * means future cadence changes don't break the type.
 *
 * Per CONTEXT D-100:
 *   - useAssetGeoJson: query, staleTime 30s, refetch-on-focus on
 *   - useCreateAsset / usePatchAsset / useSoftDeleteAsset: mutations,
 *     all invalidate the assets/geojson query so the map recolors after edits.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPatch, apiPost, ApiError } from "./client";

export type AssetType = "manhole" | "catch_basin";
export type AssetShape = "manhole" | "catchbasin"; // sprite-key form (no underscore)
export type DimensionColor = "green" | "yellow" | "red" | "grey";

export interface AssetFeatureProperties {
  id: number;
  number: string;
  type: AssetType;
  shape: AssetShape;
  description: string;
  cleaning_color: DimensionColor;
  coating_color: DimensionColor;
  cover_level_color: DimensionColor;
  pipe_blocked_color: DimensionColor;
  structure_color: DimensionColor;
}

export interface AssetFeature {
  type: "Feature";
  geometry: { type: "Point"; coordinates: [number, number] };
  properties: AssetFeatureProperties;
}

export interface AssetFeatureCollection {
  type: "FeatureCollection";
  features: AssetFeature[];
}

export const ASSETS_GEOJSON_KEY = ["assets", "geojson"] as const;

/**
 * PM-side GeoJSON read. SSE-driven invalidation since Phase 4 Plan 05
 * (RESEARCH.md Q1 RESOLVED): the cached value is treated as immediately
 * stale (`staleTime: 0`) so the next render after invalidation triggers
 * a fetch. `refetchInterval: false` makes the no-polling decision
 * explicit for future maintainers — polling at 5 s + ~350 ms server +
 * render = ~5.4 s p95, which violates the 2 s NFR-PERF-3 budget.
 *
 * The lifecycle is owned by `useAssetStateStream` in
 * `web/src/features/map/useAssetStateStream.ts`: that hook opens an
 * EventSource on `/api/admin/assets/state-changes` and calls
 * `qc.invalidateQueries({queryKey: ASSETS_GEOJSON_KEY})` on each
 * `state_changed` event.
 *
 * `refetchOnWindowFocus: true` remains as a complement to SSE — if the
 * user refocuses the tab after a long idle, we want a fresh snapshot
 * before the SSE channel reconnects (which can take a backoff cycle).
 */
export function useAssetGeoJson() {
  return useQuery<AssetFeatureCollection, ApiError>({
    queryKey: ASSETS_GEOJSON_KEY,
    queryFn: () => apiGet<AssetFeatureCollection>("/api/admin/assets/geojson"),
    staleTime: 0,
    refetchInterval: false,
    refetchOnWindowFocus: true,
  });
}

export interface AssetCreatePayload {
  number: string;
  type: AssetType;
  description: string;
  latitude: number;
  longitude: number;
}

export interface AssetPatchPayload {
  number?: string;
  type?: AssetType;
  description?: string;
  latitude?: number;
  longitude?: number;
}

interface AssetCreatedResponse {
  id: number;
  code: "OK_ASSET_CREATED";
}

interface AssetUpdatedResponse {
  code: "OK_ASSET_UPDATED";
}

interface AssetSoftDeletedResponse {
  code: "OK_ASSET_SOFT_DELETED";
}

export function useCreateAsset() {
  const qc = useQueryClient();
  return useMutation<AssetCreatedResponse, ApiError, AssetCreatePayload>({
    mutationFn: (p) => apiPost<AssetCreatedResponse>("/api/admin/assets", p),
    onSuccess: () => qc.invalidateQueries({ queryKey: ASSETS_GEOJSON_KEY }),
  });
}

export function usePatchAsset() {
  const qc = useQueryClient();
  return useMutation<
    AssetUpdatedResponse,
    ApiError,
    { id: number; payload: AssetPatchPayload }
  >({
    mutationFn: ({ id, payload }) =>
      apiPatch<AssetUpdatedResponse>(`/api/admin/assets/${id}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ASSETS_GEOJSON_KEY }),
  });
}

export function useSoftDeleteAsset() {
  const qc = useQueryClient();
  return useMutation<AssetSoftDeletedResponse, ApiError, number>({
    mutationFn: (id) => apiDelete<AssetSoftDeletedResponse>(`/api/admin/assets/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ASSETS_GEOJSON_KEY }),
  });
}
